import { useMemo } from 'react';

import classNames from 'classnames';
import { parseJSON } from 'date-fns';

import type { PortableTextComponents } from '@portabletext/react';

import ExpanderList from '@helsenorge/designsystem-react/components/ExpanderList';
import Loader from '@helsenorge/designsystem-react/components/Loader';
import Spacer from '@helsenorge/designsystem-react/components/Spacer';
import Title from '@helsenorge/designsystem-react/components/Title';
import designsystemtypography from '@helsenorge/designsystem-react/scss/typography.module.scss';

import ReferanseBlock from '@helsenorge/core-cms/behandlingsvalg/referanseblock';
import RessursBlock from '@helsenorge/core-cms/behandlingsvalg/ressursblock';
import PortableText from '@helsenorge/core-cms/richtext';
import { globalComponents } from '@helsenorge/core-cms/richtext/portable-text-utils';
import UserFeedback from '@helsenorge/core-cms/userfeedback';
import NotFound from '@helsenorge/core-framework/page-not-found';
import { getSidetittelId } from '@helsenorge/framework-utils/hn-page';

import CreditsBlock from './creditsBlock';
import SamvalgtrinnBlock from './samvalgtrinnBlock/index';
import VideoBlockPortableText from '../shared/blocks/VideoBlock/VideoBlockPortableText';
import VideoMatriseBlockPortableText from '../shared/blocks/VideoMatriseBlock/VideoMatriseBlockPortableText';
import { useSamvalgPageContent } from '../shared/useSamvalgPageContent';
import { videoLanguageMapper } from '../shared/videoLanguageMapper';

import styles from './styles.module.scss';

const createVideoBlockComponent = (subtitleLanguageCode: string): PortableTextComponents => ({
  types: {
    ...globalComponents.types,

    VideoBlock: props => <VideoBlockPortableText {...props} value={{ ...props.value, subtitleLanguageCode }} />,
    VideoMatriseBlock: props => <VideoMatriseBlockPortableText {...props} value={{ ...props.value, subtitleLanguageCode }} />,
  },
});

const Verktoy: React.FunctionComponent = () => {
  const { verktoyPage, isVerktoyPageError: isError, isVerktoyPageLoading: isLoading } = useSamvalgPageContent();

  const subtitleLanguageCode = useMemo(
    () => verktoyPage?.languageVersions && videoLanguageMapper(verktoyPage?.languageVersions),
    [verktoyPage?.languageVersions]
  );
  const videoBlockComponent = useMemo(
    () => subtitleLanguageCode && createVideoBlockComponent(subtitleLanguageCode),
    [subtitleLanguageCode]
  );

  if (isLoading) {
    return <Loader center />;
  }

  if (isError || !verktoyPage) {
    return <NotFound />;
  }

  const mainColumnClass = 'col-md-10 offset-md-1 col-lg-8 offset-lg-2';
  const introWrapperClasses = classNames(mainColumnClass, styles['introduksjon-wrapper']);

  return (
    <>
      <title>{verktoyPage.metaData.browserTitle}</title>
      <meta name="description" content={verktoyPage.metaData.metaDescription} />
      <meta name="keywords" content={verktoyPage.metaData.keywords} />
      <div className={'container'}>
        <div className="row">
          <div className={introWrapperClasses}>
            <Title margin={2} htmlMarkup="h1" appearance="title1" id={getSidetittelId()}>
              {verktoyPage.title}
            </Title>
            <p className={classNames(designsystemtypography.preamble, styles.introduksjon)}>{verktoyPage.introduction}</p>
          </div>
        </div>
      </div>

      <div className={styles.mainContentWrapper}>
        <div className={'container'}>
          <div className="row">
            <div className={mainColumnClass}>
              <ExpanderList color="cherry" renderChildrenWhenClosed>
                {verktoyPage.samvalgSteps
                  ?.filter(trinnBlock => trinnBlock.title)
                  .map((trinnBlock, i) => {
                    const title = verktoyPage.showStepNumber === true ? `${i + 1}. ${trinnBlock.title}` : trinnBlock.title;
                    return (
                      <ExpanderList.Expander key={trinnBlock.title} title={title} titleHtmlMarkup="h2">
                        <SamvalgtrinnBlock
                          key={trinnBlock.title}
                          intro={trinnBlock.introduction}
                          mainContent={trinnBlock.mainContent}
                          additionalContent={trinnBlock.additionalContent}
                          showTreatmentLinks={trinnBlock.showTreatmentLinks}
                          showComparisonAsTable={trinnBlock.showComparisonAsTable}
                          showPrint={trinnBlock.showPrint}
                          fasteTekster={verktoyPage.fasteTekster}
                          subtitleLanguageCode={subtitleLanguageCode}
                        />
                      </ExpanderList.Expander>
                    );
                  })}
              </ExpanderList>
            </div>
          </div>
        </div>
      </div>
      <div className={'container'}>
        {verktoyPage.otherContent?.map((otherContent, index) => {
          if (otherContent._type === 'VideoBlock') {
            return (
              <div className="row" key={index}>
                <div className={mainColumnClass}>
                  <Spacer size={'xl'} />
                  <PortableText value={otherContent} components={videoBlockComponent} />
                </div>
              </div>
            );
          } else if (otherContent._type === 'VideoMatriseBlock') {
            return (
              <div className="row" key={index}>
                <div className={'col-md-12  col-lg-12'}>
                  <Spacer size={'xl'} />
                  <PortableText value={otherContent} components={videoBlockComponent} />
                </div>
              </div>
            );
          } else if (otherContent.type === 'ResourceBlock') {
            return (
              <div className="row" key={index}>
                <div className={mainColumnClass}>
                  <Spacer size={'xl'} />
                  <RessursBlock resource={otherContent} />
                </div>
              </div>
            );
          } else if (otherContent._type === 'ImageBlock') {
            return (
              <div className="row" key={index}>
                <div className={'col-12'}>
                  <Spacer size={'xl'} />
                  <PortableText value={otherContent} />
                </div>
              </div>
            );
          }
        })}
        <div className="row">
          <div className={mainColumnClass}>
            <Spacer size={'xl'} />
            {verktoyPage.references && verktoyPage.referencesHeader && (
              <ReferanseBlock title={verktoyPage.referencesHeader} body={verktoyPage.references} />
            )}
          </div>
        </div>
        {verktoyPage && (
          <div className="row">
            <CreditsBlock
              sistFagligOppdatert={parseJSON(verktoyPage.sistFagligOppdatert)}
              nesteFagligOppdatering={parseJSON(verktoyPage.nesteFagligOppdatering)}
              innholdLevertAv={verktoyPage.innholdLevertAv}
              utvikletAv={verktoyPage.utvikletAv}
              finansiertAv={verktoyPage.finansiertAv}
              bidragsyterOgInteresser={verktoyPage.bidragsyterOgInteresser}
              fasteTekster={verktoyPage.fasteTekster}
            />
          </div>
        )}
      </div>
      {verktoyPage && !verktoyPage.hideUserFeedbackBlock && <UserFeedback {...verktoyPage.userFeedback} />}
    </>
  );
};

export default Verktoy;
